/**
 * TableDetailQrSection — QR code, URL and financial summary strip for TableDetail.
 */
import { useTranslation } from "react-i18next";
import { formatPrice } from "@/utils/currency";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, ExternalLink, Link2 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { TableNfcWriteButton } from "./TableNfcWriteButton";

export function TableDetailQrSection({ table, tableName, menuUrl, total, finalTotal, appliedDiscount, sessionPending, orders, sessionPayments }) {
  const { t } = useTranslation();

  const downloadQR = () => {
    const canvas = document.createElement("canvas");
    const size = 400;
    const padding = 40;
    canvas.width = size;
    canvas.height = size + 80;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#111";
    ctx.font = "bold 22px Arial, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(tableName, size / 2, 30);
    const svgEl = document.getElementById(`qr-detail-${table.id}`);
    if (svgEl) {
      const svgData = new XMLSerializer().serializeToString(svgEl);
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, padding, 42, size - padding * 2, size - padding * 2);
        ctx.fillStyle = "#666";
        ctx.font = "12px Arial, sans-serif";
        ctx.fillText("Escanea para hacer tu pedido", size / 2, size + 30);
        const link = document.createElement("a");
        link.download = `qr_${tableName.toLowerCase().replace(/\s+/g, "_")}.png`;
        link.href = canvas.toDataURL("image/png");
        link.click();
        toast.success(t("tables.qrDownloaded"));
      };
      img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
    }
  };

  return (
    <>
      {/* QR + URL */}
      <div className="flex gap-8 mb-4 items-start">
        <div className="flex-shrink-0">
          <span className="text-sm font-bold text-gray-700 block mb-2">QR Code</span>
          <div className="bg-white p-3 rounded-lg border-2 border-gray-200 inline-block">
            <QRCodeSVG id={`qr-detail-${table.id}`} value={menuUrl} size={120} level="H" />
          </div>
        </div>
        <div className="flex-1 space-y-3">
          <div>
            <span className="text-sm font-bold text-gray-700">URL</span>
            <div className="flex items-center gap-2 mt-1">
              <a href={menuUrl} target="_blank" rel="noreferrer" className="text-emerald-700 text-sm hover:underline" data-testid="detail-test-link">Abrir link (test)</a>
              <button onClick={() => { navigator.clipboard.writeText(menuUrl); toast.success("Link copiado"); }} data-testid="copy-url-btn">
                <Link2 className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600" />
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-1 break-all">{menuUrl}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="border-emerald-300 text-emerald-700 font-bold uppercase text-xs gap-1.5" onClick={downloadQR} data-testid="download-qr-btn">
              <Download className="w-3.5 h-3.5" /> DESCARGAR QR
            </Button>
            <Button variant="outline" size="sm" className="text-xs gap-1.5" onClick={() => window.open(menuUrl, "_blank")} data-testid="open-menu-btn">
              <ExternalLink className="w-3.5 h-3.5" /> ABRIR MENÚ
            </Button>
            <TableNfcWriteButton url={menuUrl} />
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="flex gap-8 mb-6 text-sm">
        <div>
          <span className="font-bold text-gray-700">Total</span>
          <p className="text-lg font-bold text-gray-900">{formatPrice(total)}</p>
        </div>
        {appliedDiscount > 0 && (
          <div>
            <span className="font-bold text-amber-600">Desconto</span>
            <p className="text-lg font-bold text-amber-700">-{formatPrice(appliedDiscount)}</p>
          </div>
        )}
        <div>
          <span className="font-bold text-gray-700">A Pagar</span>
          <p className="text-lg font-bold text-emerald-700">{formatPrice(finalTotal)}</p>
        </div>
        <div>
          <span className="font-bold text-gray-700">Pago pendiente</span>
          <p className="text-gray-600">{formatPrice(sessionPending)}</p>
        </div>
        <div>
          <span className="font-bold text-gray-700">Abertura</span>
          <p className="text-gray-600">{orders.length > 0 ? new Date(orders[0]?.created_at).toLocaleDateString("es-ES", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "-"}</p>
        </div>
        <div>
          <span className="font-bold text-gray-700">Link para pagamentos</span>
          <div className="flex items-center gap-1">
            <a href={menuUrl} target="_blank" rel="noreferrer" className="text-emerald-700 text-sm hover:underline">Abrir link</a>
            <Link2 className="w-3 h-3 text-gray-400" />
          </div>
        </div>
      </div>
    </>
  );
}
